import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext, getIn } from 'formik';
import { Grid, Collapse, IconButton } from '@material-ui/core';
import caret from 'core/assets/svg/caretOrange.svg';
import { lookupsJson, ScheduledFarmItemTypes } from '@ourbranch/lookups';
import { ActionButton } from 'core/components/action-button';
import Field from 'core/components/form/form.v2';
import InfoIcon from 'core/assets/svg/ic-info-dark.svg?react';
import deleteIcon from 'core/assets/svg/delete-icon.svg';
import Label from 'core/components/label/label';

import useStyles from './scheduled-farm-property-item.styles';

const { scheduledFarmPropertyCategories, increaseStackLimitOptions, deductibleAllOther } = lookupsJson.farm;

const ScheduledFarmPropertyItem = ({ item, index, onDelete, fieldName, coverageType, disabled, state }) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const { values: formValues, setFieldValue } = useFormikContext();

  const availableItemTypes = scheduledFarmPropertyCategories;

  // Find the FO6Scheduled risk index
  const locationRisks = getIn(formValues, `${fieldName}.risks`);
  const fo6ScheduledRiskIndex = locationRisks.findIndex((risk) => risk.type === 'FO6Scheduled');
  const itemFieldPath = `${fieldName}.risks[${fo6ScheduledRiskIndex}].coverages.${coverageType}[${index}]`;

  const currentItem = getIn(formValues, itemFieldPath) || item;

  const getItemTypeName = (categoryId) => {
    const itemType = availableItemTypes.find((type) => type.id === categoryId);
    return itemType?.value || 'Unknown';
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(index);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={classes.item}>
      <div className={classes.itemTitleRow}>
        <Grid xs={10}>
          <Label className={classes.itemName}>
            {currentItem.extendedDetails?.itemName || currentItem.description || 'Item Name'}
          </Label>
          <div className={classes.itemType}>{getItemTypeName(currentItem.category)}</div>
        </Grid>
        <div className={classes.divider} />
        {currentItem.deductible && (
          <>
            <Grid className={classes.itemStack} xs={3}>
              <Label className={classes.itemLabel}>Deductible</Label>
              <Label className={classes.itemValue}>${currentItem.deductible?.toLocaleString()}</Label>
            </Grid>
            <div className={classes.divider} />
          </>
        )}
        <Grid className={classes.itemStack} xs={3}>
          <Label className={classes.itemLabel}>Coverage</Label>
          <Label className={classes.itemValue}>${currentItem.limit?.toLocaleString()}</Label>
        </Grid>
        <Grid xs={3} container justifyContent="flex-end">
          <ActionButton
            onClick={handleDelete}
            className={classes.deleteButton}
            icon={deleteIcon}
            size="small"
            disabled={disabled}
          />
          <IconButton onClick={toggleExpanded} className={classes.expandIcon} size="small">
            <img
              className={isExpanded ? classes.icon : classes.iconFold}
              src={caret}
              alt={isExpanded ? 'fold' : 'unfold'}
            />
          </IconButton>
        </Grid>
      </div>

      <Collapse in={isExpanded}>
        <div className={classes.formContainer}>
          <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Field
              xs={6}
              type="select"
              id={`${itemFieldPath}.category`}
              name={`${itemFieldPath}.category`}
              mode="light"
              label="Type of item"
              options={availableItemTypes}
              disabled={true}
            />
            <Field
              xs={6}
              type="string"
              id={`${itemFieldPath}.description`}
              name={`${itemFieldPath}.description`}
              mode="light"
              label="Item name"
              disabled={disabled}
            />
            <Field
              xs={6}
              type="select"
              id={`${itemFieldPath}.deductible`}
              name={`${itemFieldPath}.deductible`}
              mode="light"
              label="Deductible"
              options={deductibleAllOther}
              disabled={disabled}
            />
            <Field
              xs={6}
              type="numeric"
              id={`${itemFieldPath}.limit`}
              name={`${itemFieldPath}.limit`}
              mode="light"
              label="Coverage amount"
              format={{ thousandSeparator: true, prefix: '$' }}
              disabled={disabled}
            />

            {[
              ScheduledFarmItemTypes.COMBINES,
              ScheduledFarmItemTypes.CUSTOM_FARM_MACHINERY,
              ScheduledFarmItemTypes.DESCRIBED_MACHINERY,
              ScheduledFarmItemTypes.IRRIGATION_EQUIPMENT,
              ScheduledFarmItemTypes.MACHINERY_NOT_DESCRIBED,
              ScheduledFarmItemTypes.RENTED_OR_BORROWED_EQUIPMENT
            ].includes(currentItem.category) && (
              <>
                <Field
                  xs={6}
                  type="string"
                  id={`${itemFieldPath}.serialNumber`}
                  name={`${itemFieldPath}.serialNumber`}
                  mode="light"
                  label="Serial number"
                  disabled={disabled}
                />
                <Field
                  xs={6}
                  type="string"
                  id={`${itemFieldPath}.make`}
                  name={`${itemFieldPath}.make`}
                  mode="light"
                  label="Make/model"
                  disabled={disabled}
                />
              </>
            )}

            {[
              ScheduledFarmItemTypes.COMBINES,
              ScheduledFarmItemTypes.DESCRIBED_MACHINERY,
              ScheduledFarmItemTypes.IRRIGATION_EQUIPMENT
            ].includes(currentItem.category) && (
              <>
                <Field
                  xs={6}
                  type="checkbox"
                  id={`${itemFieldPath}.hasReplacementCostCoverage`}
                  name={`${itemFieldPath}.hasReplacementCostCoverage`}
                  mode="light"
                  label="Replacement cost coverage"
                  disabled={disabled}
                />
                <Field
                  xs={6}
                  type="checkbox"
                  id={`${itemFieldPath}.hasOpenPerils`}
                  name={`${itemFieldPath}.hasOpenPerils`}
                  mode="light"
                  label="Open perils"
                  subLabel="Including intake of foreign objects"
                  disabled={disabled}
                />
              </>
            )}

            {currentItem.category === ScheduledFarmItemTypes.HAY && (
              <Field
                xs={12}
                type="select"
                id={`${itemFieldPath}.increaseStackLimit`}
                name={`${itemFieldPath}.increaseStackLimit`}
                mode="light"
                label="Increase stack limit"
                options={increaseStackLimitOptions}
                disabled={disabled}
              />
            )}

            {currentItem.category === ScheduledFarmItemTypes.LIVESTOCK && (
              <>
                <Field
                  xs={6}
                  type="select"
                  id={`${itemFieldPath}.livestockCategory`}
                  name={`${itemFieldPath}.livestockCategory`}
                  mode="light"
                  label="Livestock type"
                  options={lookupsJson.farm.livestockTypes}
                  disabled={disabled}
                  onChange={(val) => {
                    if (val !== 'cattle') {
                      setFieldValue(`${itemFieldPath}.hasSpecialCattleEndorsement`, false);
                    }
                  }}
                />
                <Field
                  xs={6}
                  type="select"
                  id={`${itemFieldPath}.additionalPerils`}
                  name={`${itemFieldPath}.additionalPerils`}
                  mode="light"
                  label="Additional Perils"
                  options={lookupsJson.farm.livestockAdditionalPerils}
                  disabled={disabled}
                />
                <Field
                  xs={6}
                  type="checkbox"
                  id={`${itemFieldPath}.hasWinterPerils`}
                  name={`${itemFieldPath}.hasWinterPerils`}
                  mode="light"
                  label="Winter perils"
                  disabled={disabled}
                />
                <Field
                  xs={6}
                  type="checkbox"
                  id={`${itemFieldPath}.hasSuffocationOfLivestock`}
                  name={`${itemFieldPath}.hasSuffocationOfLivestock`}
                  mode="light"
                  label="Suffocation of livestock"
                  disabled={disabled}
                />
                <Field
                  xs={6}
                  type="checkbox"
                  id={`${itemFieldPath}.hasSpecialCattleEndorsement`}
                  name={`${itemFieldPath}.hasSpecialCattleEndorsement`}
                  mode="light"
                  label="Special cattle endorsement"
                  subLabel="Multiple loss coverage"
                  disabled={disabled || currentItem.livestockCategory !== 'cattle'}
                />
              </>
            )}

            <Grid
              container
              justifyContent={currentItem.category === ScheduledFarmItemTypes.LIVESTOCK ? 'space-between' : 'flex-end'}
              className={classes.sectionFooter}
            >
              {currentItem.category === ScheduledFarmItemTypes.LIVESTOCK && (
                <Grid className={classes.livestockInfoTextContainer} xs={10}>
                  <InfoIcon width={16} height={16} />
                  <span className={classes.livestockInfoText}>
                    This is for livestock only. If you'd like to add animals (Under 4-H & FFA programs), please go to
                    that section.
                  </span>
                </Grid>
              )}
            </Grid>
          </Grid>
        </div>
      </Collapse>
    </div>
  );
};

ScheduledFarmPropertyItem.propTypes = {
  item: PropTypes.shape({
    category: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deductible: PropTypes.number,
    limit: PropTypes.number,
    description: PropTypes.string,
    serialNumber: PropTypes.string,
    make: PropTypes.string,
    hasReplacementCostCoverage: PropTypes.bool,
    hasOpenPerils: PropTypes.bool,
    increaseStackLimit: PropTypes.number,
    livestockCategory: PropTypes.string,
    includeWinterPeril: PropTypes.string,
    hasWinterPerils: PropTypes.bool,
    hasSuffocationOfLivestock: PropTypes.bool,
    hasSpecialCattleEndorsement: PropTypes.bool,
    extendedDetails: PropTypes.shape({
      itemName: PropTypes.string
    })
  }).isRequired,
  index: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  fieldName: PropTypes.string.isRequired,
  coverageType: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

ScheduledFarmPropertyItem.defaultProps = {
  disabled: false
};

export default ScheduledFarmPropertyItem;
